<script setup>
import DefaultModalBase from "../DefaultModalBase.vue";
import {useTicketStore} from "../../../store/tickets.js";
import {useModalStore} from "../../../store/modal.js";
import {useUserStore} from "../../../store/users.js";
import {useAuthStore} from "../../../store/auth.js";
import {computed, onMounted, ref} from "vue";

const ticketStore = useTicketStore()
const modalStore = useModalStore()
const userStore = useUserStore()
const authStore = useAuthStore()

const loading = ref(false)
const error = ref(null)
const is_assigned = ref(false)
const users = ref([])
const options = ref([])
const assigned_user = ref()

const ticketIds = computed(() => modalStore.props.ticket_ids ?? [])
const ticketCount = computed(() => ticketIds.value.length)
const isAdmin = computed(() => ['reseller-super-admin', 'reseller-team-leader', 'super-admin', 'internal-super-admin', 'internal-team-leader'].includes(authStore.user.roles[0].name))

const heading = computed(() =>
    is_assigned.value
        ? `${ticketCount.value} ticket(s) assigned.`
        : `Assign ${ticketCount.value} ticket(s).`
)

const description = computed(() =>
    is_assigned.value
        ? `Assigned to ${users.value.find(u => u.id === assigned_user.value)?.first_name ?? ''}`
        : `Select an assignee for ${ticketCount.value} ticket(s)`
)

const assignTickets = async () => {
    loading.value = true
    error.value = null

    try {
        await ticketStore.bulkAssignTickets(ticketIds.value, assigned_user.value)
        await ticketStore.refreshTickets()
        is_assigned.value = true
    } catch (e) {
        error.value = e.response?.data?.message ?? 'Could not assign the tickets.'
    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    loading.value = true
    await userStore.getAllUsers({all: false, role: 'internal-agent'})
    users.value = isAdmin.value ? userStore.users : [authStore.user]

    options.value = users.value.map(u => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name}`
    }))
    loading.value = false
})
</script>

<template>
    <DefaultModalBase :description :heading :error :icon="is_assigned ? 'check-all' : 'user-single-add'" :loading>

        <div class="ticket-assign-container">
            <brand-button variant="secondary" v-if="is_assigned" @click="modalStore.close()">Done</brand-button>
            <brand-form-select label="Assign to." :options="options" v-model="assigned_user" v-if="!is_assigned"/>
            <brand-button @click="assignTickets()" :disabled="!assigned_user" v-if="!is_assigned">Assign {{ ticketCount }} ticket(s)</brand-button>
        </div>
    </DefaultModalBase>
</template>

<style scoped lang="scss">
.ticket-assign-container {
    display: flex;
    flex-direction: column;
    gap: 3rem;
}
</style>
